interface BootWhenConfiguredOptions {
  rootElement: HTMLElement
  load: () => Promise<unknown>
  // A dynamic import(): some modules read the config at import time.
  importApp: () => Promise<{ boot: (rootElement: HTMLElement) => void }>
  failureMessage: string
  retryDelaysMs?: number[]
}

// Only "no config" is transient. By name: instanceof is unreliable on transpiled Errors.
const isRetryable = (error: unknown): boolean =>
  (error as { name?: string } | null)?.name === 'BrandConfigUnavailableError'

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const loadWithRetry = async (load: BootWhenConfiguredOptions['load'], delaysMs: number[]) => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await load()
    } catch (error) {
      if (!isRetryable(error) || attempt >= delaysMs.length) throw error
      await wait(delaysMs[attempt])
    }
  }
}

const showFailure = (rootElement: HTMLElement, message: string) => {
  const text = document.createElement('p')
  text.textContent = message
  const retry = document.createElement('button')
  retry.type = 'button'
  retry.textContent = 'Retry'
  retry.onclick = () => location.reload()
  rootElement.textContent = ''
  rootElement.appendChild(text)
  rootElement.appendChild(retry)
}

export const bootWhenConfigured = ({
  rootElement,
  load,
  importApp,
  failureMessage,
  retryDelaysMs = [1000, 3000, 9000]
}: BootWhenConfiguredOptions): Promise<void> =>
  loadWithRetry(load, retryDelaysMs)
    .then(importApp)
    .then(({ boot }) => boot(rootElement))
    .catch((error) => {
      console.error('Failed to boot:', error)
      showFailure(rootElement, failureMessage)
    })
